import React, { useState, useEffect } from "react";
import { 
    Briefcase, RefreshCw, MapPin, Plus, Edit, 
    Star, Loader2, Mail, Phone, FileText, ChevronDown
} from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import { useNavigate } from "react-router-dom";
import { 
    useGetAllJobscarrierQuery, 
    useUpdatecarrierStatusMutation,
    useUpdatecarrierFeaturedMutation,
    useLazyGetAllJobApplicantsQuery,
    useUpdateApplicationStatusMutation
} from "../../../redux/actions/Admin/footerManagementAdmin";
import { IJob } from "../../../types/Response/AdminResponse";

// Helper to color-code your C# JobApplicationStatus Enum text beautifully
const getApplicantStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
        case "applied": return "bg-blue-50 text-blue-700 border-blue-200";
        case "underreview": return "bg-amber-50 text-amber-700 border-amber-200";
        case "shortlisted": return "bg-purple-50 text-purple-700 border-purple-200";
        case "interviewscheduled": return "bg-indigo-50 text-indigo-700 border-indigo-200";
        case "rejected": return "bg-rose-50 text-rose-700 border-rose-200";
        case "hired": return "bg-green-50 text-green-700 border-green-200";
        default: return "bg-slate-50 text-slate-600 border-slate-200";
    }
};

const CareersManagement: React.FC = () => {
    const navigate = useNavigate();
    
    // Core RTK Query Hooks API Setup
    const { data: jobsResponse, isLoading: isLoadingJobs, isError, refetch } = useGetAllJobscarrierQuery();
    const [updateStatus, { isLoading: isStatusChanging }] = useUpdatecarrierStatusMutation();
    const [updateFeatured, { isLoading: isFeaturedChanging }] = useUpdatecarrierFeaturedMutation();
    const [triggerFetchApplicants, { data: applicantsResponse, isLoading: isLoadingApplicants }] = useLazyGetAllJobApplicantsQuery();


    const [updateAppStatus, { isLoading: isAppStatusChanging }] = useUpdateApplicationStatusMutation();

    console.log(applicantsResponse,'applicantsResponse');

    // Controlled Workspace Focus States
    const [selectedJob, setSelectedJob] = useState<IJob | null>(null);
    const [activeApplicantTab, setActiveApplicantTab] = useState<string>("all");

    // Correctly unpack the .NET object result value wrapper
    const jobsList = jobsResponse?.value?.data ?? [];
    const applicantsList = applicantsResponse?.value?.data ?? [];

    // Automatically focus on the first job row if none is selected
    useEffect(() => {
        if (!selectedJob && jobsList.length > 0) {
            handleSelectJobFocus(jobsList[0]);
        }
    }, [jobsList, selectedJob]);

    const handleSelectJobFocus = (job: IJob) => {
        setSelectedJob(job);
        triggerFetchApplicants(job.id);
    };

    const handleToggleActiveStatus = async (
        id: number,
        currentActiveState: boolean
      ) => {
        if (isStatusChanging) return;
      
        try {
          await updateStatus({
            id,
            isActive: !currentActiveState,
          }).unwrap();
      
          await refetch();
        } catch (error) {
          console.error(error);
        }
      };

    const handleToggleFeaturedStatus = async (id: number, currentFeaturedState: boolean) => {
        if (isFeaturedChanging) return;
        try {
            await updateFeatured({ id, isFeatured: !currentFeaturedState }).unwrap();
            if (selectedJob && selectedJob.id === id) {
                setSelectedJob(prev => prev ? { ...prev, isFeatured: !currentFeaturedState } : null);
            }
        } catch (error) {
            console.error("Failed to alter featured opening attribute:", error);
        }
    };

    // Advanced dynamic dropdown updater passing direct enum mapping integers to backend patches
    const handleStatusDropdownChange = async (applicationId: number, nextStatusCode: number) => {
        if (isAppStatusChanging || !selectedJob) return;
        try {
            await updateAppStatus({ id: applicationId, status: nextStatusCode }).unwrap();
            triggerFetchApplicants(selectedJob.id); // Refresh current applicant workspace panel
        } catch (error) {
            console.error("Failed to update applicant pipeline state:", error);
        }
    };

    // Maps your .NET Enum values gracefully to your frontend UI pipeline categories tabs
    const filteredApplicants = applicantsList.filter(app => {
        if (activeApplicantTab === "all") return true;
        
        const currentStatus = app.status?.toLowerCase();
        
        // Tab "Pending" matches Applied, UnderReview, Shortlisted, and InterviewScheduled states
        if (activeApplicantTab === "pending") {
            return (
                currentStatus === "applied" || 
                currentStatus === "underreview" || 
                currentStatus === "shortlisted" ||
                currentStatus === "interviewscheduled"
            );
        }
        
        // Tab "Approved" matches Hired state configurations
        if (activeApplicantTab === "approved") {
            return currentStatus === "hired" || currentStatus === "approved";
        }
        
        // Tab "Rejected" matches Rejected status directly
        if (activeApplicantTab === "rejected") {
            return currentStatus === "rejected";
        }
        
        return currentStatus === activeApplicantTab;
    });

    return (
        <div className="min-h-screen p-4 md:p-6 lg:p-8 antialiased text-slate-800">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* --- HEADER ACTIONS PANEL --- */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-6 border border-slate-200 gap-4 bg-white p-5 rounded-2xl shadow-xs">
                    <div className="space-y-1 text-left">
                        <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                            <Briefcase className="text-[#2F5E2E] w-6 h-6 stroke-[2.5]" />
                            Career Management
                        </h1>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-center">
                        <button 
                            onClick={() => refetch()}
                            className="p-2.5 border border-slate-200 bg-white text-slate-600 rounded-xl hover:bg-slate-50 transition cursor-pointer shadow-2xs"
                        >
                            <RefreshCw size={14} />
                        </button>
                        <CommonButton
                            text="Publish New Opening"
                            icon={<Plus size={16} />}
                            variant="filled"
                            className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-xs font-bold py-2.5"
                            onClick={() => navigate("addNewCarrer")}
                        />
                    </div>
                </div>

                {/* ================= RECRUITER SPLIT-GRID WORKSPACE ================= */}
                {!isLoadingJobs && !isError && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                        
                        {/* LEFT COLUMN: LIVE RECRUITMENT JOB TRACK Openings */}
                        <div className="lg:col-span-5 space-y-3 max-h-[calc(100vh-160px)] overflow-y-auto pr-1">
                            {jobsList.length === 0 ? (
                                <div className="bg-white border rounded-2xl p-12 text-center text-xs text-slate-400 italic border-dashed border-slate-200">
                                    No logged career entries recorded inside platform archives registers.
                                </div>
                            ) : (
                                jobsList.map((job: IJob) => {
                                    const isFocused = selectedJob?.id === job.id;
                                    return (
                                        <div
                                            key={job.id}
                                            onClick={() => handleSelectJobFocus(job)}
                                            style={{ borderColor: isFocused ? "#5FB63F" : "" }}
                                            className={`p-4 bg-white rounded-2xl border text-left transition-all duration-150 cursor-pointer relative shadow-2xs group flex flex-col justify-between select-none ${isFocused ? "ring-1 ring-[#5FB63F]/30 shadow-md border-[#5FB63F]" : "hover:border-slate-300"}`}
                                        >
                                            <div className="space-y-1">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">{job.department || "General"}</span>
                                                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                        <button 
                                                            onClick={() => handleToggleFeaturedStatus(job.id, job.isFeatured)}
                                                            className={`p-1 rounded transition-colors ${job.isFeatured ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`}
                                                        >
                                                            <Star size={13} fill={job.isFeatured ? "currentColor" : "none"} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleActiveStatus(job.id, job.isActive)}
                                                            className="relative inline-flex h-3.5 w-6 shrink-0 cursor-pointer rounded-full transition-colors duration-150"
                                                            style={{ backgroundColor: job.isActive ? "#2F5E2E" : "#CBD5E1" }}
                                                        >
                                                            <span className="inline-block h-2.5 w-2.5 transform rounded-full bg-white transition shadow-sm mt-0.5" style={{ transform: job.isActive ? "translateX(10px)" : "translateX(2px)" }} />
                                                        </button>
                                                    </div>
                                                </div>

                                                <h3 className="text-sm font-black text-slate-900 tracking-tight leading-snug truncate pr-6">{job.jobTitle}</h3>
                                                
                                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold text-slate-400 pt-1">
                                                    <span className="flex items-center gap-0.5"><MapPin size={11} /> {job.location}</span>
                                                    <span>•</span>
                                                    <span>{job.employmentType}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* ================= RIGHT COLUMN: INTERACTIVE CANDIDATE APPLICANTS PIPELINE ================= */}
                        <div className="lg:col-span-7">
                            {selectedJob ? (
                                <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm space-y-5 flex flex-col relative overflow-hidden min-h-[460px]">
                                    
                                    {(isStatusChanging || isFeaturedChanging || isAppStatusChanging || isLoadingApplicants) && (
                                        <div className="absolute inset-0 bg-white/70 backdrop-blur-xs z-30 flex flex-col items-center justify-center gap-2">
                                            <Loader2 className="w-6 h-6 animate-spin text-[#2F5E2E]" />
                                            <span className="text-xs font-bold text-slate-700">Syncing candidate registers...</span>
                                        </div>
                                    )}

                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-4 border-b border-slate-100 gap-3">
                                        <div className="text-left">
                                            <div className="flex items-center gap-2">
                                                <h2 className="text-base font-black text-slate-900 tracking-tight leading-tight">{selectedJob.jobTitle}</h2>
                                                {selectedJob.isFeatured && (
                                                    <span className="bg-amber-50 text-amber-700 border border-amber-200 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded">Featured</span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Vacancies Open Count: {selectedJob.vacancyCount || 1} open slots</p>
                                        </div>

                                        <button
                                            onClick={() => navigate(`addNewCarrer/${selectedJob.id}`)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 bg-slate-50/50 hover:bg-slate-100 rounded-xl text-xs font-bold text-slate-700 transition cursor-pointer self-start sm:self-center"
                                        >
                                            <Edit size={12} />
                                            <span>Edit Open Parameters</span>
                                        </button>
                                    </div>

                                    {/* PIPELINE TABS SELECTION CONTROLS ROW */}
                                    <div className="flex border border-slate-200 text-xs font-bold text-slate-400 bg-slate-50 rounded-xl p-1 shadow-2xs">
                                        {["all", "pending", "approved", "rejected"].map((tab) => (
                                            <button 
                                                key={tab}
                                                onClick={() => setActiveApplicantTab(tab)} 
                                                className={`flex-1 py-1.5 text-center rounded-lg transition capitalize font-bold ${activeApplicantTab === tab ? "bg-[#2F5E2E] text-white shadow-xs" : "hover:text-slate-700 cursor-pointer"}`}
                                            >
                                                {tab}
                                            </button>
                                        ))}
                                    </div>

                                    {/* PIPELINE APPLICANT SCROLL LOG CONTAINER FRAME */}
                                    <div className="flex-1 overflow-y-auto max-h-[360px] space-y-2.5 pr-1">
                                        {filteredApplicants.length === 0 ? (
                                            <div className="py-16 text-center text-xs text-slate-400 border border-dashed border-slate-100 bg-slate-50/30 rounded-2xl italic">
                                                No candidates found matching this category tab selection.
                                            </div>
                                        ) : (
                                            filteredApplicants.map((app) => (
                                                <div key={app.id} className="p-4 bg-white border border-slate-100 rounded-2xl shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition hover:border-slate-200">
                                                    <div className="space-y-1 min-w-0 flex-1 text-left">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h4 className="text-sm font-bold text-slate-900 truncate">{app.fullName}</h4>
                                                            <span className={`text-[10px] border px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${getApplicantStatusBadgeColor(app.status)}`}>
                                                                {app.status}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-col gap-0.5 text-xs text-slate-400 font-medium pt-1">
                                                            <p className="flex items-center gap-1"><Mail size={11} /> {app.email}</p>
                                                            <p className="flex items-center gap-1"><Phone size={11} /> {app.phoneNumber}</p>
                                                        </div>
                                                        
                                                        <a 
                                                            href={app.resumeUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#2F5E2E] hover:underline pt-2 mt-1 border-t border-dashed w-full border-gray-100"
                                                        >
                                                            <FileText size={12} />
                                                            <span>Review Candidate Uploaded Resume File</span>
                                                        </a>
                                                    </div>

                                                    {/* WORKFLOW STATUS DROPDOWN SELECTION (MAPPED DIRECTLY TO .NET ENUMS) */}
                                                    <div className="relative shrink-0 self-end sm:self-center border-t sm:border-t-0 pt-2.5 sm:pt-0 border-gray-50 w-full sm:w-auto justify-end flex items-center gap-2">
                                                        <span className="text-[10px] text-slate-400 font-bold uppercase hidden md:inline">Stage:</span>
                                                        <div className="relative inline-block w-full sm:w-auto">
                                                            <select
                                                                defaultValue={
                                                                    app.status?.toLowerCase() === "applied" ? 1 :
                                                                    app.status?.toLowerCase() === "underreview" ? 2 :
                                                                    app.status?.toLowerCase() === "shortlisted" ? 3 :
                                                                    app.status?.toLowerCase() === "interviewscheduled" ? 4 :
                                                                    app.status?.toLowerCase() === "rejected" ? 5 : 6
                                                                }
                                                                onChange={(e) => handleStatusDropdownChange(app.id, Number(e.target.value))}
                                                                className="w-full sm:w-40 h-9 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl px-3 text-xs font-bold text-slate-700 outline-none cursor-pointer focus:border-[#6DBE45] appearance-none pr-8 transition"
                                                            >
                                                                <option value={1}>1 - Applied</option>
                                                                <option value={2}>2 - Under Review</option>
                                                                <option value={3}>3 - Shortlisted</option>
                                                                <option value={4}>4 - Interview Scheduled</option>
                                                                <option value={5}>5 - Rejected</option>
                                                                <option value={6}>6 - Hired</option>
                                                            </select>
                                                            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                                        </div>
                                                    </div>

                                                </div>
                                            ))
                                        )}
                                    </div>

                                </div>
                            ) : (
                                <div className="bg-white border border-dashed border-slate-300 p-24 text-center rounded-3xl text-xs text-slate-400 italic">
                                    Select an open position track option card row link reference from the left panel column to read live application candidates portfolios.
                                </div>
                            )}
                        </div>

                    </div>
                )}

            </div>
        </div>
    );
};

export default CareersManagement;